import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldOff, Loader2, Plus, Trash2,
  AlertTriangle, CheckCircle2, X, Search, RefreshCw, Phone, Database, Activity, Settings as SettingsIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';

function useMe() {
  const { data } = useQuery({ queryKey: ['me'], queryFn: () => apiGet('/auth/me'), staleTime: 30_000 });
  return data?.agent || null;
}

function StatusPill({ status }) {
  const map = {
    clean:     { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', Icon: CheckCircle2 },
    dnc:       { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40',           Icon: ShieldOff },
    litigator: { cls: 'bg-rose-600/20 text-rose-200 border-rose-600/50',           Icon: ShieldAlert },
    wireless:  { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',        Icon: Phone },
    invalid:   { cls: 'bg-white/5 text-white/40 border-white/10',                  Icon: X },
    unknown:   { cls: 'bg-white/5 text-white/50 border-white/10',                  Icon: AlertTriangle },
  };
  const m = map[status] || map.unknown;
  const Ic = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${m.cls}`}>
      <Ic className="w-3 h-3" />
      {status || 'unknown'}
    </span>
  );
}

export default function DncCompliance() {
  const me = useMe();
  const isAdmin = me && (me.role === 'admin' || me.role === 'superadmin');
  const qc = useQueryClient();

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['dnc-status'],
    queryFn: () => apiGet('/dnc/status'),
    staleTime: 15_000,
  });

  const provider = status?.provider || 'none';
  const configured = !!status?.configured;
  const settings = status?.settings || { enforceLiveScrub: false, cacheTtlHours: 24, blockOn: ['dnc', 'litigator'], scrubOnImport: true, totalScrubsRun: 0 };

  // Local form state mirrors settings
  const [enforce, setEnforce] = useState(settings.enforceLiveScrub);
  const [scrubImports, setScrubImports] = useState(settings.scrubOnImport);
  const [ttl, setTtl] = useState(settings.cacheTtlHours);
  const [blockOn, setBlockOn] = useState(settings.blockOn || []);
  const [savingSettings, setSavingSettings] = useState(false);

  // Sync local state when status loads
  React.useEffect(() => {
    if (!status) return;
    setEnforce(settings.enforceLiveScrub);
    setScrubImports(settings.scrubOnImport);
    setTtl(settings.cacheTtlHours);
    setBlockOn(settings.blockOn || []);
  }, [status?.settings?.enforceLiveScrub, status?.settings?.scrubOnImport, status?.settings?.cacheTtlHours]);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      await apiPut('/dnc/settings', {
        enforceLiveScrub: enforce,
        scrubOnImport: scrubImports,
        cacheTtlHours: parseInt(ttl, 10),
        blockOn,
      });
      toast.success('DNC settings updated');
      refetchStatus();
    } catch (e) {
      toast.error(e.message || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  }

  // Tester
  const [testerPhone, setTesterPhone] = useState('');
  const [testerResult, setTesterResult] = useState(null);
  const [testerLoading, setTesterLoading] = useState(false);
  async function runTester() {
    if (!testerPhone.trim()) return;
    setTesterLoading(true);
    setTesterResult(null);
    try {
      const r = await apiPost('/dnc/scrub', { phone: testerPhone.trim() });
      setTesterResult(r.result);
    } catch (e) {
      toast.error(e.message || 'Scrub failed');
    } finally {
      setTesterLoading(false);
    }
  }

  // Internal DNC list
  const { data: list, refetch: refetchList } = useQuery({
    queryKey: ['dnc-list'],
    queryFn: () => apiGet('/dnc/list?limit=200'),
    staleTime: 5_000,
  });
  const items = list?.items || [];
  const total = list?.total || 0;

  const [newPhone, setNewPhone] = useState('');
  const [newReason, setNewReason] = useState('manual');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);
  async function addEntry() {
    if (!newPhone.trim()) return;
    setAdding(true);
    try {
      await apiPost('/dnc/list', { phone: newPhone, reason: newReason, notes: newNotes });
      toast.success('Added to DNC list');
      setNewPhone(''); setNewNotes('');
      refetchList();
    } catch (e) {
      toast.error(e.message || 'Failed to add');
    } finally {
      setAdding(false);
    }
  }

  // Bulk paste
  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  async function addBulk() {
    const phones = bulkText.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (phones.length === 0) return;
    setBulkLoading(true);
    try {
      const r = await apiPost('/dnc/list/bulk', { phones, reason: 'manual' });
      toast.success(`Added ${r.added}, skipped ${r.dupes} duplicates`);
      setBulkText('');
      refetchList();
    } catch (e) {
      toast.error(e.message || 'Bulk add failed');
    } finally {
      setBulkLoading(false);
    }
  }

  async function removeEntry(id) {
    if (!confirm('Remove this number from DNC list?')) return;
    try {
      await apiDel(`/dnc/list/${id}`);
      refetchList();
    } catch (e) {
      toast.error(e.message || 'Failed to remove');
    }
  }

  if (!me) return <div className="p-6 text-white/60"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Shield className="w-6 h-6 text-cyan-400" /> DNC & TCPA Compliance
        </h1>
        <p className="text-sm text-white/60 mt-1">
          Real-time scrub via Blacklist Alliance Litigation Firewall™. Blocks known TCPA litigators and DNC-listed numbers before any outbound call or text.
        </p>
      </div>

      {/* Provider status banner */}
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${configured ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-amber-500/5 border-amber-500/30'}`}>
        {configured ? <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0" /> : <ShieldAlert className="w-6 h-6 text-amber-400 shrink-0" />}
        <div className="flex-1">
          <div className="text-sm font-medium">
            {provider === 'blacklist_alliance' ? 'Blacklist Alliance' : provider === 'ccc' ? 'Contact Center Compliance' : 'No provider'}
            {' '}
            <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${configured ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
              {configured ? 'Connected' : 'API key not set'}
            </span>
          </div>
          <div className="text-xs text-white/60 mt-1">
            {configured ? (
              <>Live scrub active. {settings.totalScrubsRun?.toLocaleString() || 0} numbers scrubbed all-time.</>
            ) : (
              <>Set <code className="px-1 py-0.5 bg-black/30 rounded-sm">DNC_API_KEY</code> in Render env vars to activate. Sign up at <a href="https://www.blacklistalliance.com" target="_blank" className="text-cyan-400 underline">blacklistalliance.com</a>.</>
            )}
          </div>
        </div>
      </div>

      {/* Settings (admin only) */}
      {isAdmin && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-cyan-400" />
            <h2 className="font-semibold">Scrub Policy</h2>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={enforce} onChange={e => setEnforce(e.target.checked)} className="mt-1" />
            <div>
              <div className="text-sm font-medium">Enforce live scrub on every outbound</div>
              <div className="text-xs text-white/50">Calls + SMS will be blocked if the recipient is flagged. Each scrub costs ~$0.05.</div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={scrubImports} onChange={e => setScrubImports(e.target.checked)} className="mt-1" />
            <div>
              <div className="text-sm font-medium">Scrub on CSV import</div>
              <div className="text-xs text-white/50">Every imported lead is scrubbed once at upload time. Numbers flagged as DNC or litigator are auto-marked DNC.</div>
            </div>
          </label>

          <div>
            <div className="text-sm font-medium mb-2">Block when status is:</div>
            <div className="flex flex-wrap gap-2">
              {['dnc', 'litigator', 'wireless', 'invalid', 'unknown'].map(s => {
                const checked = blockOn.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setBlockOn(checked ? blockOn.filter(x => x !== s) : [...blockOn, s])}
                    className={`px-3 py-1 rounded-full text-xs border transition ${checked ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-200' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-white/40 mt-2">Recommended: dnc + litigator. Add wireless if you require explicit consent for autodialed calls.</div>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm">Cache TTL (hours):</label>
            <input
              type="number" min="1" max="168" value={ttl}
              onChange={e => setTtl(e.target.value)}
              className="w-20 px-2 py-1 rounded-sm bg-white/5 border border-white/10 text-sm"
            />
            <span className="text-xs text-white/40">Skip re-scrubbing the same number within this window.</span>
          </div>

          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-medium disabled:opacity-50"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save settings'}
          </button>
        </div>
      )}

      {/* Tester */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-cyan-400" />
          <h2 className="font-semibold">Test a number</h2>
        </div>
        <div className="flex gap-2">
          <input
            type="tel" placeholder="+15551234567"
            value={testerPhone}
            onChange={e => setTesterPhone(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runTester()}
            className="flex-1 px-3 py-2 rounded-sm bg-black/30 border border-white/10 text-sm"
          />
          <button
            onClick={runTester}
            disabled={testerLoading || !testerPhone.trim()}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm border border-white/10 disabled:opacity-50"
          >
            {testerLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Scrub'}
          </button>
        </div>
        {testerResult && (
          <div className="text-sm bg-black/30 border border-white/10 rounded-sm p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-white/60">Status:</span>
              <StatusPill status={testerResult.status} />
              <span className="text-xs text-white/40">via {testerResult.provider}</span>
            </div>
            {testerResult.raw && (
              <pre className="text-[11px] text-white/50 overflow-auto max-h-48">{JSON.stringify(testerResult.raw, null, 2)}</pre>
            )}
          </div>
        )}
      </div>

      {/* Internal DNC list */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-cyan-400" />
            <h2 className="font-semibold">Internal DNC list</h2>
            <span className="text-xs text-white/40">({total.toLocaleString()})</span>
          </div>
          <button onClick={() => refetchList()} className="text-white/50 hover:text-white/80">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div className="text-xs text-white/60 font-medium">Add single number</div>
            <input
              type="tel" placeholder="+15551234567"
              value={newPhone}
              onChange={e => setNewPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-sm bg-black/30 border border-white/10 text-sm"
            />
            <select
              value={newReason}
              onChange={e => setNewReason(e.target.value)}
              className="w-full px-3 py-2 rounded-sm bg-black/30 border border-white/10 text-sm"
            >
              <option value="manual">Manual</option>
              <option value="requested">Customer requested</option>
              <option value="do_not_call_disposition">Do-not-call disposition</option>
              <option value="federal_match">Federal DNC match</option>
              <option value="state_match">State DNC match</option>
              <option value="lead_flag">Flagged from lead</option>
              <option value="other">Other</option>
            </select>
            <input
              type="text" placeholder="Notes (optional)"
              value={newNotes}
              onChange={e => setNewNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-sm bg-black/30 border border-white/10 text-sm"
            />
            <button
              onClick={addEntry}
              disabled={adding || !newPhone.trim()}
              className="w-full px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add to DNC
            </button>
          </div>

          {isAdmin && (
            <div className="space-y-2">
              <div className="text-xs text-white/60 font-medium">Bulk paste (one per line)</div>
              <textarea
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
                placeholder="+15551234567&#10;+15559876543&#10;..."
                className="w-full px-3 py-2 rounded-sm bg-black/30 border border-white/10 text-sm font-mono"
                rows={6}
              />
              <button
                onClick={addBulk}
                disabled={bulkLoading || !bulkText.trim()}
                className="w-full px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm border border-white/10 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add all
              </button>
            </div>
          )}
        </div>

        {/* List */}
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Added by</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-white/40">No numbers on the list yet.</td></tr>
              )}
              {items.map(it => (
                <tr key={it._id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2 font-mono">{it.phone}</td>
                  <td className="px-3 py-2 text-white/60 text-xs">{it.reason}</td>
                  <td className="px-3 py-2 text-white/60 text-xs">{it.addedBy?.name || it.addedBy?.email || '—'}</td>
                  <td className="px-3 py-2 text-white/60 text-xs">{new Date(it.addedAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-right">
                    {isAdmin && (
                      <button
                        onClick={() => removeEntry(it._id)}
                        className="text-rose-400 hover:text-rose-300 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-white/5 bg-black/20 p-4 text-xs text-white/40">
        <strong className="text-white/60">How it works:</strong> When live scrub is on, every outbound call and SMS is checked against Blacklist Alliance's Litigation Firewall™ (federal + state DNC, known TCPA litigators, professional plaintiffs). Results are cached on each lead for {ttl} hours so you only pay once per number per window. Internal DNC list applies before the API call — anyone you add here is blocked instantly with no scrub charge.
      </div>
    </div>
  );
}
